import type * as React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

const STEPS = ['Upload', 'Map fields', 'Reconcile', 'Import'] as const;

export type ImportWizardStep = (typeof STEPS)[number];

export function ImportWizardProgress({
  current,
}: {
  readonly current: ImportWizardStep;
}): React.ReactElement {
  const currentIndex = STEPS.indexOf(current);
  return (
    <ol
      className="grid grid-cols-4 rounded-[12px] border border-[color:var(--color-ink-100)] bg-white px-2 py-3 shadow-[0_1px_2px_rgba(13,42,77,0.04)] sm:px-3"
      aria-label="Import progress"
      data-testid="tid.compass.importWizard.progress"
    >
      {STEPS.map((step, index) => {
        const complete = index < currentIndex;
        const active = index === currentIndex;
        return (
          <li
            key={step}
            className={cn(
              'relative flex min-w-0 items-center justify-center gap-2 px-1 text-[11.5px] font-semibold sm:px-2',
              active && 'text-[color:var(--color-navy-900)]',
              complete && 'text-[color:var(--color-navy-700)]',
              !active && !complete && 'text-[color:var(--color-ink-400)]',
            )}
            aria-current={active ? 'step' : undefined}
          >
            {index < STEPS.length - 1 ? (
              <span
                aria-hidden="true"
                className={cn(
                  'absolute left-[calc(50%+2.4rem)] right-[calc(-50%+2.4rem)] top-1/2 hidden h-px -translate-y-1/2 sm:block',
                  index < currentIndex
                    ? 'bg-[color:var(--color-navy-300)]'
                    : 'bg-[color:var(--color-ink-100)]',
                )}
              />
            ) : null}
            <span
              className={cn(
                'relative z-10 flex size-5 shrink-0 items-center justify-center rounded-full border bg-white text-[9px] shadow-[0_1px_2px_rgba(13,42,77,0.06)]',
                complete &&
                  'border-[color:var(--color-success)] bg-[color:var(--color-success-bg)] text-[color:var(--color-success)]',
                active &&
                  'border-[color:var(--color-navy-900)] bg-[color:var(--color-navy-900)] text-white',
                !active &&
                  !complete &&
                  'border-[color:var(--color-ink-200)] text-[color:var(--color-ink-400)]',
              )}
            >
              {complete ? <Check className="size-2.5" /> : index + 1}
            </span>
            <span className="relative z-10 hidden bg-white pr-1 sm:inline">
              {step}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
